/**
 * Weight components flat region card for Engine Editor — substrate × calculation
 * cell (flat-regions mode, 3 of 3 vertically stacked) per scope-doc §2 cell-by-cell
 * routing.
 *
 * Day 2 V1 4a positive-affordance content per scope-doc §2 amended bswc two-cell
 * rendering framing:
 *   - Cell card renders aggregate position view (counts + cross-cell pointer +
 *     F33 trap warning at aggregate altitude when applicable) — mirrors Atlas
 *     WeightComponentsRegion.tsx pattern verbatim per scope-doc §3.2 copy
 *     principle.
 *   - Whole card role="button" clickable → navigates to dedicated edit route
 *     `/admin/engine-editor/weight-components` (single editing surface per
 *     Decision 1 relocated to dedicated route altitude).
 *   - Cross-cell pointer "→ composed into Buy Score above" navigates to the
 *     buy-score-composition sub-region anchor (same dedicated edit route).
 *
 * Substrate: direct reuse of Atlas substrate library per Sol direction at Day 2
 * P0 (item 3 = (a) direct reuse Atlas substrate libraries; content-agnostic
 * engine-state accessor; no V2 precedent lock implied). Component fetches its
 * own substrate via fetchSubstrateCalcSubstrate on mount, uses weightComponents
 * slice (total/active/f33TrapRows aggregate counts) — same shape Atlas region
 * receives via parent SubstrateCalcCell prop-pass.
 *
 * F33 trap warning per scope-doc §6.4 amended dual treatment — aggregate-altitude
 * warning at region card render home; copy verbatim per Atlas Day 2.5 D1 (α) lock
 * disk implementation. Same warning renders at dedicated edit route table-header
 * per scope-doc §6.4 amended two render homes.
 *
 * Anchor id `#weight-components` per scope-doc §2 line 261 cross-link migration
 * target (Atlas §6.4 cross-link migrates to this anchor at Day 3 P4).
 */

import { useEffect, useState, type JSX, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, AlertTriangle } from 'lucide-react';
import {
  fetchSubstrateCalcSubstrate,
  type WeightComponentsSubstrate,
} from '../../../lib/atlas/substrateCalcSubstrate';
import { URL_ENGINE_EDITOR_WEIGHT_COMPONENTS } from '../../../lib/engine-editor/engineEditorUrls';

export default function EngineEditorWeightComponents(): JSX.Element {
  const navigate = useNavigate();
  const [substrate, setSubstrate] = useState<WeightComponentsSubstrate | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    fetchSubstrateCalcSubstrate()
      .then((s) => {
        if (!cancelled) setSubstrate(s.weightComponents);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const goPrimary = (): void => {
    navigate(URL_ENGINE_EDITOR_WEIGHT_COMPONENTS);
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goPrimary();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      id="weight-components"
      data-cell-coordinate="substrate-calculation"
      data-region-type="region"
      onClick={goPrimary}
      onKeyDown={onKey}
      className="w-full text-left border border-border bg-surface-1 rounded-lg p-4 hover:border-accent transition-colors cursor-pointer"
    >
      <div className="flex items-start gap-2">
        <Scale size={16} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-bright">Weight components</h2>
            {/* Cross-cell pointer → Buy Score composition sub-region anchor */}
            <button
              type="button"
              data-cross-cell-pointer="buy-score-composition"
              onClick={(e) => {
                e.stopPropagation();
                // Buy Score composition sub-region also cross-links to the same
                // dedicated edit route per scope-doc §2 amended; surface the
                // pointer for navigation parity with Atlas pattern.
                navigate(`${URL_ENGINE_EDITOR_WEIGHT_COMPONENTS}#buy-score-composition`);
              }}
              className="text-xs text-ink-muted hover:text-accent transition-colors cursor-pointer"
            >
              → composed into Buy Score above
            </button>
          </div>

          {/* COMPOSABILITY: per-section "Last changed X days ago" indicator slot — wires post-Day 92 */}

          {/* Aggregate position view — mirrors Atlas WeightComponentsRegion §6.4 verbatim */}
          {error ? (
            <p className="text-xs text-ink-muted italic mt-1">
              Weight components substrate currently unavailable.
            </p>
          ) : !substrate ? (
            <div className="mt-1 h-8 bg-surface-4 rounded animate-pulse" aria-label="Loading weight components substrate" />
          ) : (
            <>
              <p className="text-xs text-ink-secondary mt-1">
                ISG defines {substrate.total} candidate{' '}
                {substrate.total === 1 ? 'metric' : 'metrics'} for ranking; {substrate.active}{' '}
                active. Per-session weighting in buying sessions; tooltips surface in
                BuyingSession headers.
              </p>

              {/* F33 trap inline warning at aggregate altitude per scope-doc §6.4 amended.
                  Copy verbatim per Atlas Day 2.5 D1 (α) lock disk implementation. */}
              {substrate.f33TrapRows > 0 && (
                <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-500">
                  <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                  <span>
                    Configuration trap: {substrate.f33TrapRows}{' '}
                    {substrate.f33TrapRows === 1 ? 'weight component' : 'weight components'}{' '}
                    active but not mapped to a metric. Engine silently zeros the contribution.
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
